import { useMutation, useQuery } from 'react-query';
import message from 'antd/es/message';
import { useCallback } from 'react';
import { useCurrentAddressInfo } from '@/hooks';
import { request, toastError, queryClient, useCurrentUserId } from '@/utils';
import { useHistory } from 'react-router-dom';
import { transformGroup } from '../utils';
import produce from 'immer';

export const useCreateGroup = () => {
  const history = useHistory();
  return useMutation(
    (
      params: Pick<Group, 'name' | 'chain_id' | 'token_id' | 'collection_id' | 'market_opinion'>,
    ) => {
      return request.post<{ is_success: boolean; group: Group }>('/group/create', {
        ...params,
      });
    },
    {
      onSuccess(res) {
        if (!res?.group) {
          message.error('Failed');

          return;
        } else {
          message.success('Created');
          history.push(`/group/${res.group.id}`);
        }
      },
      onError(err: any) {
        toastError(err || 'Failed');
      },
    },
  );
};

const selectGroup = (data: { group: Group }) => transformGroup(data?.group);

export const useGroupDetail = (id?: number, options?: { onError?: () => void }) => {
  const { address } = useCurrentAddressInfo();

  return useQuery(
    ['/group', id, address],
    async () => request.get<{ group: Group }>('/group', { id: id! }),
    {
      enabled: !!id,
      cacheTime: 1000,
      select: selectGroup,
      onError(e: any) {
        console.error('fetch group detail failed', e);
        options?.onError?.();
      },
      onSuccess(res) {},
    },
  );
};

export const invalidGroupDetail = (id?: number, address?: string) => {
  queryClient.invalidateQueries(['/group', id, address]);
  queryClient.invalidateQueries(['/user/groups', address]);
};

type GroupListRes = { groups: (Group & { active_proposal_count: number })[]; total_count: number };
export const useUserJoinedGroupList = (id?: string, disabled?: boolean) => {
  const select = useCallback((data: GroupListRes) => {
    return produce(data, draft => {
      draft.groups.forEach((x, index) => {
        transformGroup(x);
      });
    });
  }, []);

  return useQuery(
    ['/user/groups', id],
    () => request.get<GroupListRes>('/user/groups', { id: id! }),
    {
      enabled: !!id && !disabled,
      cacheTime: 60 * 60 * 1000, // cache 1 hour
      staleTime: 60 * 60 * 1000, // cache 1 hour
      select,
    },
  );
};

export const useFeaturedGroupList = () => {
  const select = useCallback((data: GroupListRes) => {
    return produce(data, draft => {
      draft.groups.forEach((x, index) => {
        transformGroup(x);
      });
    });
  }, []);

  return useQuery(
    ['/group/featured_list'],
    () => request.get<GroupListRes>('/group/featured_list'),
    {
      cacheTime: 60 * 1000,
      staleTime: 60 * 1000,
      select,
    },
  );
};

// 退出
export const useLeaveGroup = (id: number) => {
  const address = useCurrentUserId();
  return useMutation(
    () => {
      return request.post<{ is_success: boolean; group: Group }>('/group/leave', {
        id,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          message.error('Failed');
          return;
        }

        message.success('Success');
        invalidGroupDetail(id, address);
      },
      onError(err: any) {
        toastError(err || 'Failed');
      },
    },
  );
};

// 离任
export const useResignGroup = (id: number) => {
  const address = useCurrentUserId();

  return useMutation(
    () => {
      return request.post<{ is_success: boolean; group: Group }>('/group/resign', {
        id,
      });
    },
    {
      onSuccess(res) {
        if (!res?.is_success) {
          message.error('Failed');
          return;
        }

        message.success('Success');
        invalidGroupDetail(id, address);
      },
      onError(err: any) {
        toastError(err || 'Failed');
      },
    },
  );
};
